import { ApiClient } from '@listening/api-contract/api-client-tag';
import { createIsomorphicFn } from '@tanstack/react-start';
import type { Context, Effect } from 'effect';
import { createEffectQueryFromManagedRuntime } from 'effect-query';
import { clientRuntime } from './client-runtime';
import { getRuntime } from './get-runtime';
import { serverRuntime } from './server-runtime.server';

// Isomorphic effect-query. The server runtime calls the API service over its
// internal URL; the client runtime hits same-origin /api via fetch. Same typed
// surface in both environments.
//
// Used for component-side queries (`useQuery(eq.queryOptions(...))`) and
// standalone mutations (`useMutation(eq.mutationOptions(...))`) that need
// typed `error.match({...})` discrimination. Collection mutation handlers
// inside `queryCollectionOptions` use `runApiPromise` directly — see
// docs/projects/active/query-stack-migration.md "Architecture" section.
//
// `createIsomorphicFn` is build-time split per environment, so resolving it
// once at module load (rather than a `getEq()` call per use) is safe: the
// server bundle only ever sees the server branch and vice-versa. Exported as a
// plain `eq` constant so callsites read `eq.queryOptions(...)` directly.
const resolveEq = createIsomorphicFn()
  .server(() => createEffectQueryFromManagedRuntime(serverRuntime))
  .client(() => createEffectQueryFromManagedRuntime(clientRuntime));

export const eq = resolveEq();

type ApiClientCtx = Context.Service.Identifier<typeof ApiClient>;

// Run an Effect program against the current runtime as a Promise. For
// collection mutation handlers (`onUpdate`/`onInsert`/`onDelete`), where the
// collection itself owns the optimistic write + rollback machinery and the
// handler just needs to fire-and-forget the API call.
//
// The cast bridges `createIsomorphicFn`'s erased return type: both server and
// client runtimes provide the ApiClient context (`ApiClient.layer` in this
// dir), so an Effect needing `ApiClientCtx` is satisfied at runtime.
export const runApiPromise = <A, E>(
  eff: Effect.Effect<A, E, ApiClientCtx>,
  options?: { readonly signal?: AbortSignal },
): Promise<A> =>
  // oxlint-disable-next-line unknown-cast/forbidden -- createIsomorphicFn erases context; both branches actually provide ApiClient
  getRuntime().runPromise(eff as unknown as Effect.Effect<A, E>, options);
